import React, { useState, useEffect, useCallback } from 'react';
import { credentialsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Award, Briefcase, ExternalLink, ShieldCheck } from 'lucide-react';

const readinessStyle = (r) => {
  const map = {
    fully_verified: { label: 'Fully Verified', c: '#059669', b: 'rgba(16,185,129,0.12)' },
    partially_verified: { label: 'Partially Verified', c: '#D97706', b: 'rgba(217,119,6,0.12)' },
    initial: { label: 'Initial', c: '#64748B', b: 'rgba(100,116,139,0.12)' },
  };
  return map[r] || map.initial;
};

export default function CredentialsManagePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await credentialsAPI.listAll();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCredential = (tokenId) => window.open(`/credentials/${tokenId}`, '_blank', 'noopener,noreferrer');

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }} data-testid="credentials-loading">Loading credentials...</div>;
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }} data-testid="credentials-manage-page">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#002A38', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Award size={26} color="#D97706" /> Credentials
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.9375rem', marginTop: '0.25rem' }} data-testid="credentials-count">
          {items.length} verified credential{items.length === 1 ? '' : 's'} issued across all your jobs
        </p>
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', background: '#FAF7F2', borderRadius: 16, border: '1px dashed #CBD5E1' }} data-testid="credentials-empty">
          <Award size={40} color="#94A3B8" style={{ margin: '0 auto 1rem' }} />
          <div style={{ fontWeight: 700, color: '#334155', fontSize: '1.0625rem' }}>No credentials issued yet</div>
          <p style={{ color: '#64748B', fontSize: '0.875rem', marginTop: '0.5rem', maxWidth: 440, margin: '0.5rem auto 0' }}>
            Issue a verified NFT credential from a candidate's profile once they've completed their evaluation stages.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {items.map((t) => {
            const rs = readinessStyle(t.readiness);
            return (
              <div
                key={t._id || t.token_id}
                data-testid={`credential-row-${t.token_id}`}
                onClick={() => openCredential(t.token_id)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.125rem 1.25rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D97706'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(217,119,6,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ECE7DE'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(217,119,6,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={20} color="#D97706" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.9375rem' }}>{t.candidate_name || t.candidate_email}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#64748B', fontSize: '0.8125rem', marginTop: '0.1875rem' }}>
                    <Briefcase size={13} /> {t.job_title || '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }} title="Verification stages completed">
                    <div style={{ fontWeight: 800, fontSize: '1.0625rem', color: '#002A38' }}>{t.stages_completed ?? 0}/4</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Stages</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: rs.b, color: rs.c }}>{rs.label}</span>
                    {t.issued_at && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.25rem' }}>{new Date(t.issued_at).toLocaleDateString()}</div>}
                  </div>
                  <ExternalLink size={17} color="#CBD5E1" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
